import { isPercentageComplete } from "utils";

import {
  FOOTPRINTING_BEEF,
  FOOTPRINTING_BEEF_CALCULATED_FOOTPRINT,
  FOOTPRINTING_BEEF_PRIMARY_DATA,
  FOOTPRINTING_CROP,
  FOOTPRINTING_CROP_ASSESSMENTS_REQUIRING_REVIEW,
  FOOTPRINTING_CROP_CALCULATED_FOOTPRINT,
  FOOTPRINTING_CROP_PRIMARY_DATA,
  FOOTPRINTING_DAIRY,
  FOOTPRINTING_DAIRY_CALCULATED_FOOTPRINT,
  FOOTPRINTING_DAIRY_PRIMARY_DATA,
  FOOTPRINTING_FARM,
  FOOTPRINTING_FARM_CALCULATED_FOOTPRINT,
  FOOTPRINTING_FARM_PRIMARY_DATA,
  FOOTPRINTING_PIGLET,
  FOOTPRINTING_PIGLET_CALCULATED_FOOTPRINT,
  FOOTPRINTING_PIGLET_PRIMARY_DATA,
  FOOTPRINTING_PORK,
  FOOTPRINTING_PORK_CALCULATED_FOOTPRINT,
  FOOTPRINTING_PORK_PRIMARY_DATA,
  KG_PER_TONNE,
} from "consts";

import { FarmFootprintReturnBody, FootprintingFormCompletionStats } from "models";

const formatPercentage = (v: number) => Number((parseFloat(v.toFixed(2)) / 100).toFixed(4));

const formatFraction = (v: number) => Number((v * 100).toFixed(2));

export const formatFractionDisplay = (v: number | null) => (v === null ? null : Math.round(v * 1000) / 10);

export const formatPercentageInput = (v: number | null) => (v === null ? null : formatPercentage(v));

export const formatFractionInput = (v: number | null) => (v === null ? null : formatFraction(v));

export const formatKgToMetricTon = (v: number | null) => (v === null ? null : v / KG_PER_TONNE);

export const formatMetricTonToKg = (v: number | null) => (v === null ? null : v * KG_PER_TONNE);

// Farm-level data feeds into every commodity's calculated footprint for the same farm and year
const LIVESTOCK_CALCULATED_FOOTPRINT_KEYS = [
  FOOTPRINTING_DAIRY_CALCULATED_FOOTPRINT,
  FOOTPRINTING_BEEF_CALCULATED_FOOTPRINT,
  FOOTPRINTING_PORK_CALCULATED_FOOTPRINT,
  FOOTPRINTING_PIGLET_CALCULATED_FOOTPRINT,
];

export const footprintingFarmPrimaryQueryKeys = (primaryDataId: string, farmId: string, year: number, isAdmin: boolean) => [
  [FOOTPRINTING_FARM_PRIMARY_DATA, farmId, year, isAdmin],
  [FOOTPRINTING_FARM_PRIMARY_DATA, primaryDataId, isAdmin],
  [FOOTPRINTING_FARM_CALCULATED_FOOTPRINT, farmId, year, isAdmin],
  [FOOTPRINTING_FARM_CALCULATED_FOOTPRINT, primaryDataId, isAdmin],
  [FOOTPRINTING_FARM, farmId, year, isAdmin],
  [FOOTPRINTING_FARM, primaryDataId, isAdmin],
  ...LIVESTOCK_CALCULATED_FOOTPRINT_KEYS.map((key) => [key, farmId, year, isAdmin]),
  // crop keys are [key, farmId, cropId, year, isAdmin], so prefix-match every crop on the farm
  [FOOTPRINTING_CROP_CALCULATED_FOOTPRINT, farmId],
];

export const footprintingCropPrimaryQueryKeys = (primaryDataId: string, farmId: string, cropId: string, year: number, isAdmin: boolean) => [
  [FOOTPRINTING_CROP_PRIMARY_DATA, farmId, cropId, year, isAdmin],
  [FOOTPRINTING_CROP_PRIMARY_DATA, primaryDataId, isAdmin],
  [FOOTPRINTING_CROP_CALCULATED_FOOTPRINT, farmId, cropId, year, isAdmin],
  [FOOTPRINTING_CROP_CALCULATED_FOOTPRINT, primaryDataId, isAdmin],
  [FOOTPRINTING_CROP, farmId, cropId, year, isAdmin],
  [FOOTPRINTING_CROP, primaryDataId, isAdmin],
  [FOOTPRINTING_CROP_ASSESSMENTS_REQUIRING_REVIEW, farmId, isAdmin],
];

export const footprintingDairyPrimaryQueryKeys = (primaryDataId: string, farmId: string, year: number, isAdmin: boolean) => [
  [FOOTPRINTING_DAIRY_PRIMARY_DATA, farmId, year, isAdmin],
  [FOOTPRINTING_DAIRY_PRIMARY_DATA, primaryDataId, isAdmin],
  [FOOTPRINTING_DAIRY_CALCULATED_FOOTPRINT, farmId, year, isAdmin],
  [FOOTPRINTING_DAIRY_CALCULATED_FOOTPRINT, primaryDataId, isAdmin],
  [FOOTPRINTING_DAIRY, farmId, year, isAdmin],
  [FOOTPRINTING_DAIRY, primaryDataId, isAdmin],
];

export const footprintingPorkPrimaryQueryKeys = (primaryDataId: string, farmId: string, year: number, isAdmin: boolean) => [
  [FOOTPRINTING_PORK_PRIMARY_DATA, farmId, year, isAdmin],
  [FOOTPRINTING_PORK_PRIMARY_DATA, primaryDataId, isAdmin],
  [FOOTPRINTING_PORK_CALCULATED_FOOTPRINT, farmId, year, isAdmin],
  [FOOTPRINTING_PORK_CALCULATED_FOOTPRINT, primaryDataId, isAdmin],
  [FOOTPRINTING_PORK, farmId, year, isAdmin],
  [FOOTPRINTING_PORK, primaryDataId, isAdmin],
];

export const footprintingPigletPrimaryQueryKeys = (primaryDataId: string, farmId: string, year: number, isAdmin: boolean) => [
  [FOOTPRINTING_PIGLET_PRIMARY_DATA, farmId, year, isAdmin],
  [FOOTPRINTING_PIGLET_PRIMARY_DATA, primaryDataId, isAdmin],
  [FOOTPRINTING_PIGLET_CALCULATED_FOOTPRINT, farmId, year, isAdmin],
  [FOOTPRINTING_PIGLET_CALCULATED_FOOTPRINT, primaryDataId, isAdmin],
  [FOOTPRINTING_PIGLET, farmId, year, isAdmin],
  [FOOTPRINTING_PIGLET, primaryDataId, isAdmin],
];

export const footprintingBeefPrimaryQueryKeys = (primaryDataId: string, farmId: string, year: number, isAdmin: boolean) => [
  [FOOTPRINTING_BEEF_PRIMARY_DATA, farmId, year, isAdmin],
  [FOOTPRINTING_BEEF_PRIMARY_DATA, primaryDataId, isAdmin],
  [FOOTPRINTING_BEEF_CALCULATED_FOOTPRINT, farmId, year, isAdmin],
  [FOOTPRINTING_BEEF_CALCULATED_FOOTPRINT, primaryDataId, isAdmin],
  [FOOTPRINTING_BEEF, farmId, year, isAdmin],
  [FOOTPRINTING_BEEF, primaryDataId, isAdmin],
];

/**
 * Users can't change a footprint once it is submitted or verified; the API rejects those writes with a 422.
 * Read-only until the progress has loaded, so a form never mounts editable and then flips (its save-on-unmount would fire).
 */
export const isFootprintReadOnly = (progress?: { footprint?: { isSubmitted?: boolean; isVerified?: boolean } }) =>
  !progress || !!progress.footprint?.isSubmitted || !!progress.footprint?.isVerified;

interface FootprintingProgressResult {
  completionRate: number;
  isSubmitted: boolean;
  readyToSubmit: boolean;
  totalPayout: number;
}

/**
 * Aggregates a farm's footprinting progress with its per-assessment stats into
 * a single submission-readiness summary.
 * @param farmProgress - The farm's footprinting progress data. When null/undefined,
 *  an all-zero, not-submitted result is returned.
 * @param formCompletionStats - Per-assessment (crop/livestock) completion stats.
 *  Only entries that have a `footprint` (i.e. were picked on the assessment
 *  selection screen) contribute to the averaged completion rate.
 * @returns A {@link FootprintingProgressResult}: `completionRate` (mean completion
 *  across the farm and contributing assessments, forced to 1 once submitted),
 *  `totalPayout` (summed payout), `isSubmitted`, and `readyToSubmit`.
 */
export const calculateFootprintingProgress = (
  farmProgress: FarmFootprintReturnBody | undefined | null,
  formCompletionStats: FootprintingFormCompletionStats[]
): FootprintingProgressResult => {
  if (!farmProgress) {
    return { isSubmitted: false, totalPayout: 0, readyToSubmit: false, completionRate: 0 };
  }

  const isSubmitted = (farmProgress.footprint?.isSubmitted ?? false) && formCompletionStats.some((cp) => cp.footprint?.isSubmitted);
  const allForms = [
    farmProgress,
    ...formCompletionStats.filter(
      // Only select footprints with a primary data row (i.e. they have been
      // picked on the assessment selection screen)
      (cpq) => cpq.footprint !== undefined
    ),
  ];

  let totalPayout = 0;
  let completionRate = 0;

  allForms.forEach((item) => {
    completionRate += (item.completionPct || 0) / allForms.length;
    totalPayout += item.completionPayout || 0;
  });

  return {
    completionRate: isSubmitted ? 1 : completionRate,
    totalPayout,
    isSubmitted,
    readyToSubmit: !isSubmitted && isPercentageComplete(farmProgress.completionPct) && formCompletionStats.some((cp) => isPercentageComplete(cp.completionPct)),
  };
};
